<div class="vsc-detail vsc-print-root">

  <!-- ── Toolbar ─────────────────────────────────────────────────────────── -->
  <div class="vsc-detail__toolbar">

    <!-- Left: heading + shop code search + shop-view box -->
    <div class="vsc-detail__toolbar-left">
      <h1 class="vsc-detail__heading">Vendor Scorecard</h1>
      <!-- Shop code search — available to every user (shop and GSC alike) -->
      <div class="vsc-detail__shop-code-col">
        <div class="vsc-detail__shop-code-input">
          <mc-input
            fit="small"
            hiddenlabel
            placeholder="Shop code"
            [value]="shopCodeInput()"
            [attr.invalid]="shopCodeError() ? true : null"
            (input)="onShopCodeInput($event)"
            (keydown)="onShopCodeKeydown($event)">
          </mc-input>
        </div>
        @if (shopCodeError()) {
          <span class="vsc-detail__shop-code-error mds-text--small-normal">{{ shopCodeError() }}</span>
        }
      </div>
      @if (!isShopUser) {
        <div class="vsc-detail__shop-view-box">
          <mc-switch label="" (change)="onShopViewToggle($event)"></mc-switch>
          <span class="vsc-detail__shop-view-label">Shop View</span>
        </div>
      }
    </div>

    <!-- Right: period picker + expand/collapse combined -->
    <div class="vsc-detail__toolbar-right">

      <!-- Period selector -->
      <div class="vsc-period" #periodContainer>
        <button type="button" class="vsc-period__trigger" (click)="togglePeriodDropdown()">
          <mc-icon icon="calendar" size="16"></mc-icon>
          <span>{{ periodLabel() }}</span>
          <mc-icon [attr.icon]="periodDropdownOpen() ? 'chevron-up' : 'chevron-down'" size="16"></mc-icon>
        </button>

        @if (periodDropdownOpen()) {
          <div class="vsc-period__dropdown">
            <div class="vsc-period__tabs">
              <button
                type="button"
                class="vsc-period__tab"
                [class.vsc-period__tab--active]="periodType() === 'weekly'"
                (click)="selectPeriodType('weekly')">
                Weekly
              </button>
              <button
                type="button"
                class="vsc-period__tab"
                [class.vsc-period__tab--active]="periodType() === 'monthly'"
                (click)="selectPeriodType('monthly')">
                Monthly
              </button>
            </div>

            @if (periodType() === 'monthly') {
              <div class="vsc-period__scroll-list">
                <p class="vsc-period__section-label mds-text--small-normal mds-neutral--weak__text-color">Select Month</p>
                @for (m of monthOptions; track m) {
                  <button
                    type="button"
                    class="vsc-period__option"
                    [class.vsc-period__option--selected]="m === selectedMonth()"
                    (click)="selectMonth(m)">
                    {{ m }}
                  </button>
                }
              </div>
            }

            @if (periodType() === 'weekly') {
              <p class="vsc-period__section-label mds-text--small-normal mds-neutral--weak__text-color">Select Week · {{ weekYear }}</p>
              <div class="vsc-period__week-grid">
                @for (w of weekOptions; track w) {
                  <button
                    type="button"
                    class="vsc-period__week-cell"
                    [class.vsc-period__week-cell--selected]="w === selectedWeek()"
                    (click)="selectWeek(w)">
                    {{ w }}
                  </button>
                }
              </div>
            }
          </div>
        }
      </div>

      <!-- Expand All + Collapse All — single bordered group -->
      <div class="vsc-expand-group">
        <button
          type="button"
          class="vsc-expand-group__btn"
          [class.vsc-expand-group__btn--active]="!hasAnyExpanded()"
          (click)="expandAll()">
          <mc-icon icon="expand" size="16"></mc-icon>
          Expand All
        </button>
        <span class="vsc-expand-group__divider"></span>
        <button
          type="button"
          class="vsc-expand-group__btn"
          [class.vsc-expand-group__btn--active]="hasAnyExpanded()"
          (click)="collapseAll()">
          <span class="vsc-icon-flip">
            <mc-icon icon="expand" size="16"></mc-icon>
          </span>
          Collapse All
        </button>
      </div>

      <!-- Download — expands everything, then opens the browser print dialog -->
      <mc-button
        data-cy="vsc-download"
        fit="small"
        appearance="primary"
        icon="tray-arrow-down"
        [loading]="isPreparingDownload()"
        [disabled]="isPreparingDownload()"
        (click)="onDownloadClicked()"
        (keydown.enter)="onDownloadClicked()">
        Download
      </mc-button>

    </div>
  </div>

  <!-- Print-only title block: replaces the toolbar, which is suppressed in the export -->
  <div class="vsc-detail__print-header">
    <img class="vsc-detail__print-badge" src="assets/images/star.svg" alt="Maersk" />
    <span class="vsc-detail__print-title">Vendor Scorecard — {{ vendorCode() || '—' }}</span>
    <span class="vsc-detail__print-meta">
      {{ periodLabel() }} &nbsp;·&nbsp; {{ isShopView() ? 'Shop View' : 'KPI View' }} &nbsp;·&nbsp; Generated {{ printGeneratedAt() }}
    </span>
  </div>

  <!-- ── Subtitle ────────────────────────────────────────────────────────── -->
  <p class="vsc-detail__subtitle mds-text--small-normal">
    <span class="mds-neutral--weak__text-color">
      Scores Compiled on {{ compiledAt() ? formatCompiledAt(compiledAt()!) : '—' }}
      @if (compiledAt()) {
        <span class="vsc-detail__local-time">(local time)</span>
      }
    </span>
  </p>

  <!-- ── Overall Performance + Category Performance (two 50% columns) ────── -->
  @if (categoriesError()) {
    <div class="vsc-detail__overall-row">
      <div class="vsc-detail__error-banner">
        <p class="mds-neutral--weak__text-color mds-text--medium-normal">{{ categoriesError() }}</p>
      </div>
    </div>
  } @else if (!isLoadingCategories() && vendorCode() && displayCategories().length === 0) {
    <div class="vsc-detail__empty">
      <p class="mds-neutral--weak__text-color mds-text--medium-normal">No data found</p>
    </div>
  } @else {
    <div class="vsc-detail__overall-row">
      <div class="vsc-detail__overall-col">
        <app-overall-performance-card
          [vendorCode]="vendorCode()"
          [selectedMonthLabel]="effectiveMonthLabel()"
          [isShopView]="isShopView()"
          (compiledAt)="onCompiledAt($event)">
        </app-overall-performance-card>
      </div>
      <div class="vsc-detail__overall-col">
        <app-category-performance-card
          [categories]="displayCategories()"
          [isShopView]="isShopView()"
          [isLoading]="isLoadingCategories()">
        </app-category-performance-card>
      </div>
    </div>
  }

  <!-- ── Category cards ─────────────────────────────────────────────────── -->
  @if (isLoadingCategories()) {
    <div class="vsc-detail__empty">
      <mc-loading-indicator label="Loading scorecard" hiddenlabel variant="ring" fit="small"></mc-loading-indicator>
    </div>
  }

  @if (displayCategories().length > 0) {
    <div class="vsc-detail__categories">
      <div class="vsc-detail__col">
        @for (cat of displayCategories(); track cat.categoryId; let i = $index) {
          @if (i % 2 === 0) {
            <app-vsc-category-card [data]="cat" />
          }
        }
      </div>
      <div class="vsc-detail__col">
        @for (cat of displayCategories(); track cat.categoryId; let i = $index) {
          @if (i % 2 === 1) {
            <app-vsc-category-card [data]="cat" />
          }
        }
      </div>
    </div>
  }

</div>
